import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icons from '../../assets/icons/icon';
import jucoLogo from '../../assets/images/Logo_JUCO_header.png';
import { ThemeToggle } from '../common/ThemeToggle';
import { useTheme } from '../common/ThemeProvider';

// Navbar Component
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isThemed } = useTheme();

  // Current page's link is highlighted in the blue interactive accent.
  const linkColor = (href) => (href === pathname ? 'text-cyan-400' : 'text-white hover:text-cyan-400');

  // On themed pages the bar is transparent over the hero, then turns solid navy
  // once the page scrolls so the white logo and links stay readable.
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBackground = isThemed
    ? isScrolled || isMenuOpen
      ? 'bg-jp-band/95 backdrop-blur-md border-b border-white/10 shadow-sm'
      : 'bg-transparent border-b border-transparent'
    : 'bg-transparent backdrop-blur-sm border-b border-slate-700/30';

  const navLinks = [
    { name: 'HOME', href: '/', type: 'route' },
    { name: 'FOR PLAYERS', href: '/for-players', type: 'route' },
    { name: 'FOR COACHES', href: '/for-coaches', type: 'route' },
    { name: 'PRICING', href: '/pricing', type: 'route' },
    { name: 'ABOUT US', href: '/about', type: 'route' },
    // { name: 'SUCCESS STORIES', href: '/success-stories', type: 'route' },
    // { name: 'HOW IT WORKS', href: '#how', type: 'anchor' },
    // { name: 'BLOG', href: '#blog', type: 'anchor' },
    { name: 'SUPPORT', href: '/support', type: 'route' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${navBackground} font-roboto overflow-visible transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 overflow-visible">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-4 overflow-visible">
          {/* Left Side: Logo + Navigation */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 overflow-visible">
            {/* Logo - trimmed of transparent padding so it fits inside the bar */}
            <Link to="/" className="flex items-center flex-shrink-0 mr-2 lg:mr-4">
              <img src={jucoLogo} alt="JUCO Pipeline" className="h-10 sm:h-12 lg:h-14 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-0">
              {navLinks.map((link) => (
                link.type === 'route' ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    aria-current={link.href === pathname ? 'page' : undefined}
                    className={`px-1.5 xl:px-2 py-2 text-[10px] xl:text-[12px] font-medium ${linkColor(link.href)} transition-colors whitespace-nowrap`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="px-1.5 xl:px-2 py-2 text-[10px] xl:text-[12px] font-medium text-white hover:text-cyan-400 transition-colors whitespace-nowrap"
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>
          </div>

          {/* Right Side: Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-2">
            <ThemeToggle className="mr-1" />
            {/* <a
              href="http://jucopipeline.myshopify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded hover:from-cyan-300 hover:to-blue-400 transition-all whitespace-nowrap"
            >
              JUCO Pipeline Shop
            </a> */}
            <button
              onClick={() => navigate('/auth/login')}
              className="px-3 py-2 text-xs font-medium text-white hover:text-cyan-400 transition-colors whitespace-nowrap"
            >
              SIGN IN
            </button>
            <button
              onClick={() => navigate('/auth/signup')}
              className="px-3 py-2 text-xs font-medium bg-transparent border-2 border-white text-white rounded hover:bg-white hover:text-slate-900 transition-all whitespace-nowrap"
            >
              SIGN UP
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-1">
            <ThemeToggle />
            <button
              className="p-2 text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <Icons.Close /> : <Icons.Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={`lg:hidden backdrop-blur-md border-t ${isThemed ? 'bg-jp-band/95 border-white/10' : 'bg-slate-900/95 border-slate-700/50'}`}>
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              link.type === 'route' ? (
                <Link
                  key={link.name}
                  to={link.href}
                  aria-current={link.href === pathname ? 'page' : undefined}
                  className={`block px-3 py-2 text-sm font-medium ${linkColor(link.href)} transition-colors`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-3 py-2 text-sm font-medium text-white hover:text-cyan-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              )
            ))}
            <div className="pt-3 space-y-2 border-t border-slate-700 mt-3">
              {/* <a
                href="http://jucopipeline.myshopify.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full px-4 py-2 text-sm font-medium bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded hover:from-cyan-300 hover:to-blue-400 transition-all text-center"
              >
                JUCO Pipeline Shop
              </a> */}
              <button
                onClick={() => { navigate('/auth/login'); setIsMenuOpen(false); }}
                className="w-full px-4 py-2 text-sm font-medium text-white hover:text-cyan-400 transition-colors text-left"
              >
                SIGN IN
              </button>
              <button
                onClick={() => { navigate('/auth/signup'); setIsMenuOpen(false); }}
                className="w-full px-4 py-2 text-sm font-medium bg-transparent border-2 border-white text-white rounded hover:bg-white hover:text-slate-900 transition-all"
              >
                SIGN UP
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;